/**
 * Local Preferences
 * AsyncStorage-backed cache for device-only preferences.
 * Never synced to the server. App owns these entirely.
 * All fields stored in a single JSON key for atomic read/write.
 */

import { PreferencesStorage, type PreferencesData } from '../generated/sync/sync-config';
import { createLogger } from '../utils/logger';

const log = createLogger('LocalPreferences');

export type ColorModePreference = 'light' | 'dark' | 'system';
export type AudioMode = 'mute' | 'auto_play';
export type TTSProvider = 'device' | 'azure-server';

export interface LocalPreferences {
  colorMode: ColorModePreference;
  audioMode: AudioMode;
  ttsAutoPlay: boolean;
  ttsProvider: TTSProvider;
  ttsVoice: string;
  /**
   * UI language preference: 'source' | 'target' when the user made an
   * explicit choice via the settings toggle; '' when unset — the UI then
   * follows OS language detection (see i18n/index.ts). Never default this
   * to 'source'/'target': a merged-in default is indistinguishable from a
   * user choice and permanently disables OS detection (bugs.md 2026-07-16).
   */
  uiLanguage: string;
}

const DEFAULTS: LocalPreferences = {
  colorMode: 'system',
  audioMode: 'auto_play',
  ttsAutoPlay: true,
  ttsProvider: 'azure-server',
  ttsVoice: 'en-US-JennyNeural',
  uiLanguage: '',
};

/** In-memory cache for synchronous access (loaded once at startup). */
let _cached: LocalPreferences = { ...DEFAULTS };
let _loaded = false;

/**
 * Load local preferences from AsyncStorage.
 * Call once at app startup. Returns the loaded preferences.
 */
export async function loadLocalPreferences(): Promise<LocalPreferences> {
  try {
    const stored = await PreferencesStorage.load();
    if (stored) {
      _cached = { ...DEFAULTS, ...stored };
    }
  } catch (error) {
    log.warn('Failed to load local preferences', { error });
  }
  _loaded = true;
  return _cached;
}

/**
 * Get current local preferences (synchronous, from memory cache).
 * Returns defaults if not yet loaded.
 */
export function getLocalPreferences(): LocalPreferences {
  return { ..._cached };
}

/**
 * Whether local preferences have been loaded from AsyncStorage.
 */
export function isLocalPreferencesLoaded(): boolean {
  return _loaded;
}

/**
 * Update one or more local preference fields (read-merge-write).
 * Updates both the in-memory cache and AsyncStorage.
 */
export async function updateLocalPreferences(
  updates: Partial<LocalPreferences>,
): Promise<void> {
  _cached = { ..._cached, ...updates };
  try {
    // Load current storage to preserve fields not in LocalPreferences (e.g. activeCourseId)
    const current = await PreferencesStorage.load();
    await PreferencesStorage.save({ ...current, ..._cached } as PreferencesData);
  } catch (error) {
    log.warn('Failed to save local preferences', { error });
  }
}

/**
 * Clear local preferences (e.g., for testing).
 */
export async function clearLocalPreferences(): Promise<void> {
  _cached = { ...DEFAULTS };
  _loaded = false;
  try {
    await PreferencesStorage.clear();
  } catch (error) {
    log.warn('Failed to clear local preferences', { error });
  }
}
